import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import SweetAlert from "react-bootstrap-sweetalert";
import { Card, CardBody, Col, Container, FormGroup, Row } from "reactstrap";
import { AvField } from "availity-reactstrap-validation";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import cellEditFactory, { Type } from "react-bootstrap-table2-editor";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, BadgePlus, CheckCircle, Copy, Package, Trash2 } from "lucide-react";

import { fetchTrackingStatuses } from "../../api/trackingApi";
import Breadcrumbs from "../../components/Breadcrumb";
import PageLoader from "../../components/Loaders/pageLoader";
import { MainContent } from "../../components/Table/MainContent";
import TrackingColumnPrTest from "../../components/TrackingColumnPrTest";
import { showToast } from "../../components/Toast";
import { LIVE_LINK } from "../../helpers/url_helper";
import { priorityUkApi } from "../../services/PriorityUkService";

export default function PriorityUkraine({ profileData, loading }) {
  const [filters, setNoDbFilters] = useState({});

  const [tableData, setTableData] = useState([]);
  const [whManagersTableData, setWhManagersTableData] = useState([]);
  const [slManagersTableData, setSlManagersTableData] = useState([]);

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [modalNewOrder, setModalNewOrder] = useState(false);

  const [formValues, setFormValues] = useState({
    invoiceNo: "",
    clientName: "",
    manager: profileData?._id || "",
    address: "",
    trackingNo: "",
    comments: "",
  });

  const [orderDate, setOrderDate] = useState(() => new Date().toLocaleDateString("en-GB"));

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);

  /*
   * Если статусы не выбраны, backend получает старый фильтр
   * empty_or_packaged.
   */
  const statusQuery = filters?.status?.length > 0 ? filters.status : "empty_or_packaged";

  const {
    data = {},
    isLoading,
    error,
    refetch,
  } = priorityUkApi.useFetchAllPriorityUkQuery(
    {
      status: statusQuery,
    },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const [addPriorityUk, { isLoading: adding }] = priorityUkApi.useAddPriorityUkMutation();

  const [updatePriorityUk] = priorityUkApi.useUpdatePriorityUkMutation();

  const [deletePriorityUk] = priorityUkApi.useDeletePriorityUkMutation();

  /*
   * Заполняем локальные данные таблицы.
   */
  useEffect(() => {
    if (!data.priorityUk || !data.whManagers) {
      return;
    }

    setTableData(JSON.parse(JSON.stringify(data.priorityUk)));

    setWhManagersTableData(JSON.parse(JSON.stringify(data.whManagers)));

    setSlManagersTableData(JSON.parse(JSON.stringify(data.slManagers || [])));
  }, [data]);

  /*
   * Устанавливаем текущего пользователя менеджером
   * при создании заказа.
   */
  useEffect(() => {
    setFormValues((current) => ({
      ...current,
      manager: current.manager || profileData?._id || "",
    }));
  }, [profileData?._id]);

  /*
   * MainContent использует высоту карточки,
   * поэтому измеряем её так же, как в PriorityPL.
   */
  useEffect(() => {
    if (!cardRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;

        if (newHeight > 0) {
          setCardHeight(newHeight);
        }
      }
    });

    resizeObserver.observe(cardRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /*
   * SSE обновление tracking status.
   */
  useEffect(() => {
    const evtSource = new EventSource(`${LIVE_LINK}/priority/stream`);

    evtSource.onmessage = (event) => {
      const { data: trackingUpdate } = JSON.parse(event.data);

      setTableData((currentRows) =>
        currentRows.map((row) => {
          const trackingNumbers = Array.isArray(row.trackingNo) ? row.trackingNo : [];

          if (!trackingNumbers.includes(trackingUpdate.number)) {
            return row;
          }

          return {
            ...row,
            trackingInfo: {
              ...row.trackingInfo,

              [trackingUpdate.number]: {
                status: trackingUpdate.status,
                countryFrom: trackingUpdate.countryFrom,
                countryTo: trackingUpdate.countryTo,
              },
            },
          };
        }),
      );
    };

    evtSource.onerror = (streamError) => {
      console.error("Priority Ukraine SSE error:", streamError);

      evtSource.close();
    };

    return () => {
      evtSource.close();
    };
  }, []);

  /*
   * Клиентские фильтры MainContent.
   */
  const filteredData = useMemo(() => {
    let result = [...tableData];

    /*
     * Если статус не выбран, не показываем sent.
     */
    if (filters?.status?.length > 0) {
      result = result.filter((row) => filters.status.includes(row.status));
    } else {
      result = result.filter((row) => row.status !== "sent");
    }

    /*
     * Фильтр Warehouse Manager.
     */
    if (filters?.whManager?.length > 0) {
      result = result.filter((row) => {
        const managerId = typeof row.whManager === "object" ? row.whManager?._id : row.whManager;

        return filters.whManager.includes(managerId);
      });
    }

    return result;
  }, [filters, tableData]);

  /*
   * Формат опций, который ожидает MainContent.
   */
  const whManagerFilterOptions = useMemo(
    () =>
      whManagersTableData.map((manager) => ({
        key: manager._id,
        label: `${manager.fname || ""} ${manager.sname || ""}`.trim(),
        icon: "",
        colorClass: "bg-primary",
      })),
    [whManagersTableData],
  );

  const toggleNewOrderModal = useCallback(() => {
    setModalNewOrder((current) => !current);
  }, []);

  const handleNewOrderClick = useCallback(() => {
    setFormValues({
      invoiceNo: "",
      clientName: "",
      manager: profileData?._id || "",
      address: "",
      trackingNo: "",
      comments: "",
    });

    setOrderDate(new Date().toLocaleDateString("en-GB"));

    setModalNewOrder(true);
  }, [profileData?._id]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  /*
   * Обновление значения в таблице.
   */
  const handleSave = async (oldValue, newValue, row, column) => {
    if (oldValue === newValue) {
      return;
    }

    const field = column.dataField;

    const selectedManager = whManagersTableData.find((manager) => manager._id === newValue);

    const bitrixID = selectedManager?.bitrixID || null;

    const updatedRow = {
      _id: row._id,
      [field]: newValue,
      invoiceNo: row.invoiceNo,
      clientName: row.clientName,
      sl_bitrixID: row.managers?.bitrixID,

      ...(bitrixID
        ? {
            bitrixID,
          }
        : {}),
    };

    /*
     * Нормализуем tracking numbers в массив.
     */
    if (field === "trackingNo") {
      updatedRow.trackingNo =
        typeof newValue === "string"
          ? newValue
              .split(/[, .\n]+/)
              .map((number) => number.trim())
              .filter((number) => /\d/.test(number))
          : Array.isArray(newValue)
            ? newValue
            : [];
    }

    /*
     * Сначала обновляем UI.
     */
    setTableData((currentRows) =>
      currentRows.map((item) =>
        item._id === row._id
          ? {
              ...item,

              [field]: field === "trackingNo" ? updatedRow.trackingNo : newValue,
            }
          : item,
      ),
    );

    try {
      await updatePriorityUk(updatedRow).unwrap();

      if (field === "trackingNo" && updatedRow.trackingNo?.length > 0) {
        await fetchTrackingStatuses(updatedRow.trackingNo, "register");
      }

      showToast("Update has been done");
    } catch (updateError) {
      console.error("Failed to update Priority Ukraine row:", updateError);

      /*
       * Откат локального значения.
       */
      setTableData((currentRows) =>
        currentRows.map((item) =>
          item._id === row._id
            ? {
                ...item,
                [field]: oldValue,
              }
            : item,
        ),
      );

      showToast("Failed to update row", "error");
    }
  };

  const getWhManagerName = (id, whManagers = []) => {
    if (!id) {
      return "";
    }

    const idString = typeof id === "object" ? String(id._id) : String(id);

    const manager = whManagers.find((item) => String(item._id) === idString);

    if (!manager) {
      return "";
    }

    return `${manager.fname || ""} ${manager.sname || ""}`.trim();
  };

  const confirmDelete = (id) => {
    setRowToDelete(id);
    setShowDeleteAlert(true);
  };

  const handleDelete = async () => {
    if (!rowToDelete) {
      return;
    }

    try {
      await deletePriorityUk(rowToDelete).unwrap();

      showToast("Row deleted successfully");

      setRowToDelete(null);
      setShowDeleteAlert(false);
    } catch (deleteError) {
      console.error("Failed to delete Priority Ukraine row:", deleteError);

      showToast("Failed to delete row", "error");
    }
  };

  const STATUS_OPTIONS = [
    {
      key: "packaged",
      label: "Packaged",
      icon: <Package size={18} className="me-2" />,
      colorClass: "bg-primary",
    },
    {
      key: "sent",
      label: "Sent",
      icon: <CheckCircle size={18} className="me-2" />,
      colorClass: "bg-success bg-gradient",
    },
    {
      key: "stopped",
      label: "Stopped",
      icon: <AlertCircle size={18} className="me-2" />,
      colorClass: "bg-danger bg-gradient",
    },
  ];

  const getStatusMeta = (key) => STATUS_OPTIONS.find((status) => status.key === key);

  const columns = [
    {
      text: "ID",
      dataField: "_id",
      sort: true,
      csvExport: false,
      editable: false,

      formatter: (cell, row, rowIndex) => rowIndex + 1,
    },

    {
      text: "Client Name",
      dataField: "clientName",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.clientName}</h5>,
    },

    {
      text: "InvoiceNo",
      dataField: "invoiceNo",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.invoiceNo}</h5>,
    },

    {
      text: "Staff",
      dataField: "whManager",
      sort: true,
      editable: false,

      csvFormatter: (cell, row) => getWhManagerName(row.whManager || cell, data?.whManagers),

      formatter: (cellContent, order) => {
        const managerValue = typeof order.whManager === "object" ? order.whManager?._id || "" : order.whManager || "";

        const canSelectManager = profileData?.accType === "Admin" || profileData?.department?.depName === "Sales Department";

        return (
          <div className="form-group d-flex align-items-center">
            <select
              className="form-control wh-manager-selection me-2"
              value={managerValue}
              name="whManager"
              onChange={(event) =>
                handleSave(managerValue, event.target.value, order, {
                  dataField: "whManager",
                })
              }
              disabled={!canSelectManager}
            >
              <option value="" className="alert-primary">
                SELECT MANAGER
              </option>

              {whManagersTableData.map((manager) => (
                <option key={manager._id} value={manager._id}>
                  {manager.fname} {manager.sname}
                </option>
              ))}
            </select>

            {!managerValue && <AlertCircle size={20} color="orange" title="Manager is not selected" className="ms-2" />}
          </div>
        );
      },

      style: {
        width: "370px",
      },
    },

    {
      text: "Date",
      dataField: "date",
      sort: true,
      csvExport: true,
      hidden: false,

      csvFormatter: (cell, row) => (row.date != null ? new Date(row.date).toLocaleDateString("en-GB") : ""),

      formatter: (cell) => {
        if (!cell) {
          return "";
        }

        const dateObject = cell instanceof Date ? cell : new Date(cell);

        if (Number.isNaN(dateObject.getTime())) {
          return "";
        }

        return dateObject.toLocaleDateString("en-GB");
      },

      editor: {
        type: Type.DATE,
      },
    },

    {
      text: "Status",
      dataField: "status",
      sort: true,
      editable: false,

      formatter: (cellContent, order) => {
        const meta = getStatusMeta(order.status);

        const isAdmin = profileData?.accType === "Admin";

        const isLogisticsUA = profileData?.department?.depName === "Logistics Ukraine";

        const warehouseManagerId = typeof order.whManager === "object" ? order.whManager?._id : order.whManager;

        const isWhManager = String(warehouseManagerId || "") === String(profileData?._id || "");

        const canEdit = isAdmin || (isLogisticsUA && isWhManager) || isWhManager;

        const hasRealizationNo = Boolean(String(order.realizationNo || "").trim());

        return (
          <div className="form-group d-flex flex-column">
            {meta && (
              <div className={`color-box ${meta.colorClass} p-2 mt-2 rounded-pill text-center text-white fw-bold`}>
                {meta.icon}

                <span>{meta.label}</span>
              </div>
            )}

            {canEdit && (
              <div className="mt-2 d-flex align-items-center">
                <select
                  className="form-control status-selection"
                  value={order.status || ""}
                  onChange={(event) => {
                    const newStatus = event.target.value;

                    if (newStatus === "sent" && !hasRealizationNo) {
                      return;
                    }

                    handleSave(order.status, newStatus, order, {
                      dataField: "status",
                    });
                  }}
                >
                  <option value="">Set Status</option>

                  {STATUS_OPTIONS.map((option) => {
                    const isSentDisabled = option.key === "sent" && !hasRealizationNo;

                    return (
                      <option
                        key={option.key}
                        value={option.key}
                        disabled={isSentDisabled}
                        className={isSentDisabled ? "status-option-disabled-danger" : ""}
                        style={
                          isSentDisabled
                            ? {
                                color: "red",
                                fontWeight: 700,
                              }
                            : undefined
                        }
                      >
                        {isSentDisabled ? "⚠ Sent - Realization No required" : option.label}
                      </option>
                    );
                  })}
                </select>

                {!order.status && <BadgePlus size={20} color="orange" className="ms-2" />}
              </div>
            )}
          </div>
        );
      },

      style: {
        width: "170px",
      },
    },

    {
      text: "Address",
      dataField: "address",
      sort: true,
      editable: true,
      csvExport: true,

      formatter: (cellContent, order) => (
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="text-break name-width">{order.address}</h5>

          {order.address && (
            <Copy
              size={20}
              color="#497ea7"
              style={{
                cursor: "pointer",
              }}
              onClick={(event) => {
                event.stopPropagation();

                navigator.clipboard
                  .writeText(order.address)
                  .then(() => {
                    showToast("Address copied to clipboard!");
                  })
                  .catch(() => {
                    showToast("Failed to copy address", "error");
                  });
              }}
            />
          )}
        </div>
      ),
    },

    {
      text: "Realization No",
      dataField: "realizationNo",
      sort: true,
      editable: true,

      csvFormatter: (cell, row) => row.realizationNo ?? "",

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.realizationNo ?? ""}</h5>,
    },

    {
      text: "Tracking No",
      dataField: "trackingNo",
      sort: true,
      editable: true,

      formatter: (cellContent, order) => <TrackingColumnPrTest tableData={order} />,

      style: {
        width: "370px",
      },
    },

    {
      text: "Sales Manager",
      dataField: "managers",
      sort: true,
      hidden: false,
      editable: false,

      csvFormatter: (cell, row) => {
        const manager = Array.isArray(row.managers) ? row.managers.find(Boolean) : row.managers;

        if (!manager) {
          return "";
        }

        return `${manager.fname || ""} ${manager.sname || ""}`.trim();
      },

      formatter: (cellContent, order) => {
        const manager = Array.isArray(order.managers) ? order.managers.find(Boolean) : order.managers;

        return <h5 className="font-size-16 mb-1">{manager ? `${manager.fname || ""} ${manager.sname || ""}`.trim() : ""}</h5>;
      },
    },

    {
      text: "Comments",
      dataField: "comments",
      sort: true,
      editable: true,
      csvExport: true,

      formatter: (cellContent, order) => <h5 className="font-size-16 mb-1 text-center">{order.comments}</h5>,
    },

    {
      text: "Actions",
      dataField: "actions",
      isDummyField: true,
      editable: false,
      csvExport: false,

      formatter: (cellContent, order) => {
        const canDelete = profileData?.accType === "Admin" || profileData?.department?.depName === "Sales Department";

        if (!canDelete) {
          return "";
        }

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Trash2
              size={20}
              color="red"
              style={{
                cursor: "pointer",
              }}
              title="Delete"
              onClick={() => confirmDelete(order._id)}
            />
          </div>
        );
      },
    },
  ];

  const defaultSorted = [
    {
      dataField: "date",
      order: "asc",
    },
  ];

  const ddmmyyyyToZulu = (dateValue) => {
    const [day, month, year] = dateValue.split("/");

    return `${year}-${month}-${day}T00:00:00.000Z`;
  };

  /*
   * Создание нового заказа.
   */
  const handleAddSubmit = async (event, values) => {
    try {
      const trackingNo = (values.trackingNo || "")
        .split(/[, .\n]+/)
        .map((value) => value.trim())
        .filter(Boolean);

      const payload = {
        date: ddmmyyyyToZulu(values.orderDate),

        invoiceNo: (values.invoiceNo || "").trim(),

        clientName: (values.clientName || "").trim(),

        address: (values.address || "").trim(),

        trackingNo,

        comments: (values.comments || "").trim(),

        koruMSID: String(values.manager || profileData?._id || "").trim(),
      };

      await addPriorityUk(payload).unwrap();

      showToast("Order created");

      setModalNewOrder(false);

      setFormValues({
        invoiceNo: "",
        clientName: "",
        manager: profileData?._id || "",
        address: "",
        trackingNo: "",
        comments: "",
      });

      refetch?.();
    } catch (addError) {
      console.error("Failed to create Priority Ukraine order:", addError);

      showToast("Failed to create order", "error");
    }
  };

  /*
   * Содержимое модального окна MainContent.
   */
  const modalNewOrderContent = (
    <>
      <Row>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="orderDate"
              label="Date"
              mask="99/99/9999"
              value={orderDate}
              onChange={(event) => setOrderDate(event.target.value)}
              placeholder="DD/MM/YYYY"
              className="form-control input-mask"
              validate={{
                required: {
                  value: true,
                  errorMessage: "Date is required",
                },

                pattern: {
                  value: /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,

                  errorMessage: "Use DD/MM/YYYY",
                },
              }}
            />
          </FormGroup>
        </Col>

        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="invoiceNo"
              label="Invoice No"
              type="text"
              value={formValues.invoiceNo}
              onChange={handleFormChange}
              validate={{
                required: {
                  value: true,
                  errorMessage: "Invoice No is required",
                },

                pattern: {
                  value: /^[-\w./\\]+$/,

                  errorMessage: "Only letters, numbers, dash, underscore and separators",
                },
              }}
            />
          </FormGroup>
        </Col>

        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              name="clientName"
              label="Client Name"
              type="text"
              value={formValues.clientName}
              onChange={handleFormChange}
              placeholder="Компания/Клиент"
              validate={{
                required: {
                  value: true,
                  errorMessage: "Client Name is required",
                },
              }}
            />
          </FormGroup>
        </Col>

        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="manager"
              className="form-select"
              label="Manager"
              value={formValues.manager || profileData?._id || ""}
              onChange={handleFormChange}
              validate={{
                required: {
                  value: true,
                  errorMessage: "Manager is required",
                },
              }}
            >
              <option value="">Choose</option>

              {slManagersTableData.map((manager) => (
                <option key={manager._id} value={manager._id}>
                  {manager.fname} {manager.sname}
                </option>
              ))}
            </AvField>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField name="address" label="Address" type="textarea" rows="3" value={formValues.address} onChange={handleFormChange} placeholder="Адрес доставки" />
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="trackingNo"
              label="Tracking No"
              type="textarea"
              rows="3"
              value={formValues.trackingNo}
              onChange={handleFormChange}
              placeholder="Можно несколько: через запятую, пробел, точку или с новой строки"
            />
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField name="comments" label="Comments" type="textarea" rows="3" value={formValues.comments} onChange={handleFormChange} placeholder="Комментарий" />
          </FormGroup>
        </Col>
      </Row>
    </>
  );

  if (error) {
    return <p>Error loading data</p>;
  }

  return (
    <React.Fragment>
      {isLoading && <PageLoader />}

      <div className="page-content">
        <MetaTags>
          <title>Priority Ukraine</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title="Korupharma" breadcrumbItem="Priority Ukraine" />

          <Row className="card-section">
            <Col lg="12">
              <div className="pcss3t logtb pcss3t-effect-scale pcss3t-theme-1">
                <ul>
                  <AnimatePresence key="priority-ukraine" exitBeforeEnter>
                    <motion.li
                      className="tab-content tab-content-1 typography"
                      initial={{
                        opacity: 0,
                        x: 30,
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                      }}
                      exit={{
                        opacity: 0,
                        x: -30,
                      }}
                      transition={{
                        duration: 0.3,
                      }}
                      style={{
                        display: "block",
                      }}
                    >
                      <div id="card-section" ref={cardRef}>
                        <Row className="card-section">
                          <Col lg="12">
                            <Card>
                              <CardBody className="cardBodySticky">
                                <ToolkitProvider keyField="_id" columns={columns} data={filteredData} search>
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{
                                          pg: "priorityUkraine",
                                          props,
                                          cardHeight,
                                          showSearch: true,
                                          showCSV: true,
                                          showInTblFltr: true,
                                          showDbFilter: false,
                                          showBtn: true,
                                          columns: props.baseProps.columns,
                                          data: props.baseProps.data,
                                          defaultSorted,
                                          cellEdit: cellEditFactory({
                                            mode: "click",
                                            blurToSave: true,
                                            afterSaveCell: handleSave,
                                          }),
                                        }}
                                        filterOptions={{
                                          status: STATUS_OPTIONS,
                                          whManager: whManagerFilterOptions,
                                        }}
                                        // notificationSettings={{
                                        //   showNotification: true,
                                        //   keyword: "prukorder",
                                        //   module: "Bot Priority Notification",
                                        // }}
                                        setNoDbFilters={setNoDbFilters}
                                        btnCrtModalOptions={{
                                          btnLabelCrt: "Add Order",
                                          modalSize: "lg",
                                          modalTitle: "Add Order",
                                          modalBtnLabel: adding ? "Adding..." : "Add Order",
                                          modalAdd: modalNewOrder,
                                          toggleAddModal: toggleNewOrderModal,
                                          handleNewClick: handleNewOrderClick,
                                          modalContent: modalNewOrderContent,
                                          handleSubmit: handleAddSubmit,
                                        }}
                                        tableOptions={{
                                          includeSelect: false,
                                        }}
                                      />
                                      <SweetAlert
                                        show={showDeleteAlert}
                                        danger
                                        showCancel
                                        confirmBtnText="Yes, delete it!"
                                        confirmBtnBsStyle="danger"
                                        title="Are you sure?"
                                        onConfirm={handleDelete}
                                        onCancel={() => {
                                          setShowDeleteAlert(false);
                                          setRowToDelete(null);
                                        }}
                                        focusCancelBtn
                                      >
                                        The order will be deleted from the system!
                                      </SweetAlert>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
